import type { OrigemDoChamado } from '../../types/chamado'

/**
 * De onde o dado veio e até quando ele vai — as duas contas que o carimbo de procedência exibe.
 *
 * Ficam aqui, fora do componente, por dois motivos. O primeiro é que a pergunta "até quando isto
 * vai?" é a mesma na listagem e no fechamento, e duas respostas para ela é como uma tela carimba
 * 23/05 e a de trás carimba 31/05 sobre o mesmo dado. O segundo é o teste: a comparação de
 * instantes tem uma armadilha silenciosa (abaixo) que merece ser conferida sem montar tela.
 */

/**
 * A data de corte de um conjunto de registros: o instante mais recente a que o dado se refere.
 *
 * Compara por milissegundo e **não** por texto. Instante em ISO-8601 quase sempre ordena certo como
 * string, e é aí que está a armadilha: `2026-05-23T14:14:09.123Z` é MENOR que
 * `2026-05-23T14:14:09Z` na ordem alfabética, porque o ponto vem antes do "Z" na tabela de
 * caracteres. Jackson omite os milissegundos quando eles são zero, então as duas formas convivem na
 * mesma resposta — e o erro apareceria só de vez em quando, deslocando a data de corte um segundo
 * para trás sem que nada quebrasse.
 *
 * @param instantes os `coletadoEm` dos registros exibidos
 * @returns o mais recente deles, ou nulo quando não há nenhum
 */
export function dataDeCorte(instantes: readonly (string | null | undefined)[]): string | null {
  let escolhido: string | null = null
  let maior = Number.NEGATIVE_INFINITY

  for (const instante of instantes) {
    if (!instante) {
      continue
    }
    const quando = Date.parse(instante)
    if (Number.isNaN(quando) || quando <= maior) {
      continue
    }
    maior = quando
    escolhido = instante
  }

  return escolhido
}

/**
 * As procedências presentes num conjunto de registros, sem repetir e na ordem em que apareceram.
 *
 * Ordem de aparição, e não ordenação alfabética: o carimbo escreve "vieram de A e B", e a frase
 * ficaria trocando de forma a cada página se a ordem dependesse do nome.
 *
 * @param origens as procedências de cada registro exibido
 * @returns as procedências distintas
 */
export function procedenciasDe(origens: readonly OrigemDoChamado[]): OrigemDoChamado[] {
  return [...new Set(origens)]
}
